import {
  Activity,
  ArrowLeft,
  Book,
  Bot,
  FileBarChart,
  LayoutDashboard,
  LogOut,
  Menu,
  Target,
  TrendingUp,
  UploadCloud,
  UsersRound,
  Shield,
  ShieldCheck,
  BrainCircuit,
  MessageSquare,
  MessageSquareHeart,
  Coins,
  Wallet,
  ClipboardCheck,
  FlaskConical,
  X,
  Moon,
  Sun,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { logPageView } from "../api";
import { useDashboard } from "../context/DashboardContext";
import { PageHeaderSlotProvider } from "../context/PageHeaderSlotContext";
import type { ClientObjective } from "../types";
import { ClientSelector } from "./ClientSelector";
import { HealthPopover } from "./HealthCluster";

const navItems = [
  { to: "/", label: "Morning Check", icon: LayoutDashboard, page: "morning_check" },
  { to: "/cliente", label: "Cliente", icon: UsersRound, page: "cliente" },
  { to: "/social", label: "Social", icon: MessageSquareHeart, page: "social" },
  { to: "/reporte-camilia", label: "Reporte", icon: FileBarChart, page: "reporte_cuenta" },
  { to: "/conversaciones", label: "Conversaciones Bot", icon: MessageSquare, page: "conversaciones" },
  { to: "/sandbox", label: "Sandbox", icon: FlaskConical, page: "sandbox" },
  { to: "/gertrudis", label: "Gertrudis", icon: BrainCircuit, page: "gertrudis", gertrudisOnly: true },
  { to: "/notebook", label: "Wolf Notebook", icon: Book, page: "notebook", notebookOnly: true },
  { to: "/semrush", label: "Semrush", icon: UploadCloud, page: "semrush_upload", semrushOnly: true },
  { to: "/compliance", label: "Auditoría GTM", icon: Shield, page: "gtm_compliance" },
  { to: "/uso", label: "Uso", icon: Activity, page: "usage_monitor", usageOnly: true },
  { to: "/objetivos-roas", label: "Objetivos/ROAS", icon: TrendingUp, page: "objetivos_roas", objectiveRoasOnly: true },
  { to: "/presupuestos", label: "Presupuestos", icon: Coins, page: "presupuestos", budgetPlanOnly: true },
  { to: "/costos", label: "Costos", icon: Wallet, page: "costos", costsOnly: true },
  { to: "/golden", label: "Golden sets", icon: ClipboardCheck, page: "golden", goldenOnly: true },
];

let lastLoggedView = "";

/** El objetivo del cliente es el criterio de priorización de todo el tablero:
 *  vive en el header, junto al resto del contexto, y no dentro de un tab. */
function ObjectiveChip({ objective, selectedClient }: { objective?: ClientObjective; selectedClient: string }) {
  if (selectedClient === "ALL") return null;
  if (!objective?.declared || !objective.primary) {
    return (
      <span className="objective-chip is-missing" title="Nadie declaró el objetivo de este cliente en sys_config.parametros_clientes (module='NEGOCIO')">
        <Target size={14} aria-hidden="true" />
        <span>
          <small>Objetivo</small>
          <strong>Sin declarar</strong>
        </span>
      </span>
    );
  }
  const { primary, secondary, detail, event, monthlyTarget } = objective;
  const tooltip = [
    `Objetivo primario: ${primary.label}`,
    secondary ? `Secundario: ${secondary.label}` : null,
    event ? `Se mide con: ${event}` : null,
    monthlyTarget != null ? `Meta mensual: ${monthlyTarget.toLocaleString("es-CL")}` : null,
    detail,
  ].filter(Boolean).join("\n");
  return (
    <span className={`objective-chip${primary.familia ? ` is-${primary.familia.toLowerCase()}` : ""}`} title={tooltip}>
      <Target size={14} aria-hidden="true" />
      <span>
        <small>Objetivo</small>
        <strong>{primary.label}</strong>
      </span>
      {monthlyTarget != null && <em>{monthlyTarget.toLocaleString("es-CL")}/mes</em>}
    </span>
  );
}

export function AppShell() {
  const { data, session, selectedClient, selectedClientHealthState } = useDashboard();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const [pageSlotLeft, setPageSlotLeft] = useState<HTMLDivElement | null>(null);
  const [pageSlotRight, setPageSlotRight] = useState<HTMLDivElement | null>(null);
  const [pageSlotActions, setPageSlotActions] = useState<HTMLDivElement | null>(null);

  // Tema: oscuro por defecto, con soporte completo Light-Tech (#E8EBEF / #FFFFFF)
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    try {
      const saved = window.localStorage.getItem("wolf.theme");
      if (saved === "light" || saved === "dark") return saved;
    } catch {
      /* fallback */
    }
    return "dark";
  });

  useEffect(() => {
    if (theme === "light") {
      document.body.classList.add("light-mode");
      document.documentElement.setAttribute("data-theme", "light");
    } else {
      document.body.classList.remove("light-mode");
      document.documentElement.setAttribute("data-theme", "dark");
    }
    try {
      window.localStorage.setItem("wolf.theme", theme);
    } catch {
      /* fallback */
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((curr) => (curr === "light" ? "dark" : "light"));
  };

  const isClientScoped = Boolean(session?.isClientUser || session?.isClientPortal);

  // El portal de cliente ya trae Cliente y Social en su propia barra; aquí solo aparecen los módulos extra que se le asignen.
  const visibleNavItems = navItems
    .filter((item) => !isClientScoped || (item.page !== "cliente" && item.page !== "social"))
    .filter((item) => Boolean(session?.modulos?.includes(item.page)))
    .filter((item) => {
        return (
          (!item.usageOnly || session?.canViewUsage) &&
          (!item.objectiveRoasOnly || session?.canViewObjectiveRoas) &&
          (!item.semrushOnly || session?.canUploadSemrush) &&
          (!item.gertrudisOnly || session?.canViewGertrudis) &&
          (!item.notebookOnly || session?.canViewNotebook) &&
          (!item.budgetPlanOnly || session?.canEditBudgetPlan) &&
          (!item.costsOnly || session?.canViewCosts) &&
          (!item.goldenOnly || session?.canEditGolden)
        );
      });
  const activePage =
    visibleNavItems.find((item) =>
      item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to),
    )?.page ?? (isClientScoped ? "cliente" : "unknown");

  useEffect(() => {
    const key = `${activePage}::${selectedClient}`;
    if (lastLoggedView === key) return;
    lastLoggedView = key;
    logPageView(activePage, selectedClient === "ALL" ? null : selectedClient);
  }, [activePage, selectedClient]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const health = useMemo(
    () => (selectedClient === "ALL" ? undefined : data?.health[selectedClient]),
    [data, selectedClient],
  );

  const objective = useMemo(
    () => (selectedClient === "ALL"
      ? undefined
      : data?.clients.find((client) => client.clientSlug === selectedClient)?.objective),
    [data, selectedClient],
  );

  const onClientRoute = location.pathname.startsWith("/cliente");
  const onSocialRoute = location.pathname.startsWith("/social");

  return (
    <div className={`app-shell${onSocialRoute ? " is-social" : ""}`}>
      <header className="topbar">
        <div className="topbar-inner">
          {onSocialRoute && isClientScoped ? (
            <div className="reputation-desk-brand-toggle" style={{ cursor: "default", pointerEvents: "none" }}>
              <span className="reputation-desk-brand-title">Social Wolf</span>
            </div>
          ) : onSocialRoute ? (
            <button
              className="reputation-desk-brand-toggle"
              type="button"
              aria-label="Abrir navegación"
              aria-expanded={navOpen}
              onClick={() => setNavOpen((value) => !value)}
              title="Abrir navegación"
            >
              <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
                <rect width="30" height="30" rx="8" fill="#FFFFFF" />
                <path d="M8 10.5h14M8 15h10M8 19.5h6" stroke="#16181D" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
              <Menu className="rd-hamburger" size={24} aria-hidden="true" />
              <span className="reputation-desk-brand-title">Social Wolf</span>
            </button>
          ) : (
            !isClientScoped && (
              <button
                className="mobile-nav-toggle"
                type="button"
                aria-label="Abrir navegación"
                aria-expanded={navOpen}
                onClick={() => setNavOpen((value) => !value)}
              >
                <Menu size={18} />
              </button>
            )
          )}

          <ClientSelector />

          {selectedClient !== "ALL" && onClientRoute && !isClientScoped && (
            <Link className="icon-button" to="/" aria-label="Volver al Morning Check" title="Volver al Morning Check">
              <ArrowLeft size={16} />
            </Link>
          )}

          <div className="topbar-page-row-left" ref={setPageSlotLeft} />

          <span className="topbar-spacer" />

          {!isClientScoped && <ObjectiveChip objective={objective} selectedClient={selectedClient} />}

          {!isClientScoped && !onSocialRoute && (
            <HealthPopover health={health} healthState={selectedClientHealthState} selectedClient={selectedClient} />
          )}

          <div className="topbar-page-row-right" ref={setPageSlotRight} />

          <div className="topbar-page-row-actions" ref={setPageSlotActions} />

          <button
            type="button"
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={theme === "light" ? "Cambiar a modo oscuro" : "Cambiar a modo claro (Light-Tech)"}
            aria-label="Alternar modo claro u oscuro"
          >
            {theme === "light" ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          <a className="profile-button" href="/auth/logout" title={session?.email ?? "Sesión"}>
            <span>{session?.name?.charAt(0) || session?.email?.charAt(0) || "W"}</span>
            <LogOut size={14} />
          </a>
        </div>
      </header>

      {!isClientScoped && navOpen && <div className="nav-drawer-backdrop" onClick={() => setNavOpen(false)} />}
      {!isClientScoped && (
        <nav className={navOpen ? "nav-drawer is-open" : "nav-drawer"} aria-label="Navegación">
          <div className="nav-drawer-head">
            <span className="brand-mark">W</span>
            <button type="button" className="nav-drawer-close" aria-label="Cerrar navegación" onClick={() => setNavOpen(false)}>
              <X size={18} />
            </button>
          </div>
          {visibleNavItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) => (isActive ? "nav-link is-active" : "nav-link")}
            >
              <Icon size={16} strokeWidth={2.2} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      )}

      <main className="main-content">
        <PageHeaderSlotProvider value={{ left: pageSlotLeft, actions: pageSlotActions, right: pageSlotRight }}>
          <Outlet />
        </PageHeaderSlotProvider>
      </main>
    </div>
  );
}
